import { calculateAllWalls } from '../utils/panelLayout';
import { Hari, Panel, PanelConfig } from '../types';
import { applyMaterialSpec, getMaterialSpec } from '../utils/materialSpecs';
import { SegRule, FillSpec, fillSegment } from '../utils/segmentFill';

// 도면 치수(mm)·배율(px/mm)·여백(px) — configureEngine으로 설정 (기본: 하리 테스트 화면)
export let H = 5000, V = 5000, PT = 100;
export let SCALE = 0.20;
export let PADDING = 60;
export const s = (mm: number) => mm * SCALE;

// 테스트 화면 재질: SP 규격 (표준 폭·반절·최소 분할 크기)
export const MATERIAL = 'SP' as const;
export const CONFIG: PanelConfig = applyMaterialSpec({
  CONFIG_CD: 'DEFAULT', CONFIG_NM: '기본',
  DEFAULT_SIZE: 900, MIN_SIZE: 300, MAX_SIZE: 900, PANEL_SPACE: 4,
  PANEL_DEPTH_1: 75, PANEL_DEPTH_2: 100,
  DOOR_WIDTH: 800, DOOR_LEFT_PANEL: 150, DOOR_RIGHT_PANEL: 150,
}, MATERIAL);
export const HALF_SIZE = CONFIG.HALF_SIZE!;

export const PANEL_FILL = '#CCCCCC';
export let PT_PX = s(PT);
export let TD = PT_PX * 0.5;

export let panelCounter = 0;

export interface DrawnPanel {
  num: number;
  cx: number; cy: number;
  pl: number; pt: number;
  angle: number;
  lt: string; rt: string;
  labelSize: number; // 표시 사이즈 (mm)
}

export let drawnPanels: DrawnPanel[] = [];
export let currentOverrides: Record<number, { lt?: string; rt?: string; size?: number }> = {};
// CW 그리기 중 하리 내벽 줄(drawHariPanelLine) 기록: 패널 번호 → 줄 정보 (사이즈 수정 검증용)
export type HariLine = { length: number; startNum: number; base: number[] };
export let hariLineRec: Record<number, HariLine> | null = null; // 기록 중인 대상 (CW 그리기에서만)
export let cwHariLines: Record<number, HariLine> = {};           // 마지막 CW 그리기 결과
export let currentRules: Record<string, SegRule> = {}; // 구간 채우기 규칙 (CW)

export function getTypeLabel(lt: string, rt: string): string {
  if (lt === 'yangsu' && rt === 'eumsu') return '1';
  if (lt === 'eumsu' && rt === 'eumsu') return '2';
  if (lt === 'yangsu' && rt === 'yangsu') return '3';
  if (lt === 'yangsu' && rt === 'corner') return '4';
  if (lt === 'eumsu' && rt === 'corner') return '5';
  if (lt === 'corner' && rt === 'yangsu') return '6';
  if (lt === 'corner' && rt === 'eumsu') return '7';
  if (lt === 'eumsu' && rt === 'yangsu') return '8';
  if (lt === 'yangsu' && rt === 'none') return '13';
  if (lt === 'eumsu' && rt === 'none') return '14';
  if (lt === 'none' && rt === 'yangsu') return '15';
  if (lt === 'none' && rt === 'eumsu') return '16';
  if (lt === 'corner' && rt === 'none') return '17';
  if (lt === 'none' && rt === 'corner') return '18';
  if (lt === 'none' && rt === 'none') return '19';
  return `${lt[0]}/${rt[0]}`;
}

export function drawPanel(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  pl: number, pt: number,
  angle: number,
  lt: string, rt: string,
  label: string
) {
  panelCounter++;
  const num = panelCounter;
  // 오버라이드 적용
  const ovr = currentOverrides[num];
  if (ovr) {
    if (ovr.lt) lt = ovr.lt;
    if (ovr.rt) rt = ovr.rt;
    if (ovr.size) {
      pl = s(ovr.size);
      label = `${ovr.size}`;
    }
  }
  const typeLabel = getTypeLabel(lt, rt);

  drawnPanels.push({ num, cx, cy, pl, pt, angle, lt, rt, labelSize: parseInt(label) || Math.round(pl / SCALE) });

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  ctx.translate(-pl / 2, -pt / 2);

  ctx.fillStyle = PANEL_FILL; ctx.fillRect(0, 0, pl, pt);
  ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.strokeRect(0, 0, pl, pt);

  if (lt === 'yangsu') {
    ctx.beginPath(); ctx.moveTo(0,0); ctx.lineTo(-TD, pt/2); ctx.lineTo(0, pt);
    ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle='#333'; ctx.stroke();
  } else if (lt === 'corner') {
    ctx.beginPath(); ctx.moveTo(pt, pt); ctx.lineTo(pt/2, TD); ctx.lineTo(0, pt);
    ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle='#333'; ctx.stroke();
  } else if (lt === 'eumsu') {
    ctx.beginPath(); ctx.moveTo(0,0); ctx.lineTo(TD, pt/2); ctx.lineTo(0, pt);
    ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle='#333'; ctx.stroke();
  }
  if (rt === 'yangsu') {
    ctx.beginPath(); ctx.moveTo(pl,0); ctx.lineTo(pl+TD, pt/2); ctx.lineTo(pl, pt);
    ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle='#333'; ctx.stroke();
  } else if (rt === 'eumsu') {
    ctx.beginPath(); ctx.moveTo(pl,0); ctx.lineTo(pl-TD, pt/2); ctx.lineTo(pl, pt);
    ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle='#333'; ctx.stroke();
  } else if (rt === 'corner') {
    ctx.beginPath(); ctx.moveTo(pl-pt, pt); ctx.lineTo(pl-pt/2, TD); ctx.lineTo(pl, pt);
    ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle='#333'; ctx.stroke();
  }

  ctx.fillStyle = '#333'; ctx.font = `bold ${Math.max(10, pt*0.65)}px Arial`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  // 라벨이 뒤집혀 보이지 않도록 180도 보정 (가로=정방향, 세로=아래→위 통일)
  ctx.save();
  ctx.translate(pl / 2, pt / 2);
  const normAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  if (normAngle >= Math.PI / 2 - 0.001 && normAngle < Math.PI * 1.5 - 0.001) ctx.rotate(Math.PI);
  ctx.fillText(label, 0, 0);
  ctx.restore();

  ctx.restore();

  // 뱃지: 패널 아래쪽에 표시 (사이즈 텍스트/치수선과 겹침 방지)
  const badgeR = 7;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // 패널 하단 중앙에서 아래로 충분히 떨어진 위치
  const badgeOffset = pt / 2 + badgeR + 12;
  const bx = cx + sin * badgeOffset;
  const by = cy - cos * badgeOffset;
  ctx.save();
  ctx.fillStyle = '#e53935';
  ctx.beginPath();
  ctx.arc(bx, by, badgeR, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 8px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${num}`, bx, by);
  ctx.restore();

  ctx.save();
  ctx.fillStyle = '#1565c0';
  ctx.font = 'bold 8px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(`(${typeLabel}번)`, bx + badgeR + 2, by);
  ctx.restore();
}

export function drawDimension(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number,
  label: string, offset: number = 15, color: string = '#333'
) {
  const isHoriz = Math.abs(y2 - y1) < 2;
  const dx = isHoriz ? 0 : offset;
  const dy = isHoriz ? offset : 0;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1;
  ctx.font = 'bold 10px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.beginPath();
  ctx.moveTo(x1 + dx, y1 + dy);
  ctx.lineTo(x2 + dx, y2 + dy);
  ctx.stroke();

  ctx.setLineDash([2, 2]);
  ctx.beginPath();
  ctx.moveTo(x1, y1); ctx.lineTo(x1 + dx, y1 + dy);
  ctx.moveTo(x2, y2); ctx.lineTo(x2 + dx, y2 + dy);
  ctx.stroke();
  ctx.setLineDash([]);

  const arrowSize = 4;
  if (isHoriz) {
    ctx.beginPath();
    ctx.moveTo(x1 + dx, y1 + dy);
    ctx.lineTo(x1 + dx + arrowSize, y1 + dy - arrowSize);
    ctx.lineTo(x1 + dx + arrowSize, y1 + dy + arrowSize);
    ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x2 + dx, y2 + dy);
    ctx.lineTo(x2 + dx - arrowSize, y2 + dy - arrowSize);
    ctx.lineTo(x2 + dx - arrowSize, y2 + dy + arrowSize);
    ctx.closePath(); ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(x1 + dx, y1 + dy);
    ctx.lineTo(x1 + dx - arrowSize, y1 + dy + arrowSize);
    ctx.lineTo(x1 + dx + arrowSize, y1 + dy + arrowSize);
    ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x2 + dx, y2 + dy);
    ctx.lineTo(x2 + dx - arrowSize, y2 + dy - arrowSize);
    ctx.lineTo(x2 + dx + arrowSize, y2 + dy - arrowSize);
    ctx.closePath(); ctx.fill();
  }

  const mx = (x1 + x2) / 2 + dx;
  const my = (y1 + y2) / 2 + dy;
  if (isHoriz) {
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(mx - 20, my - 7, 40, 14);
    ctx.fillStyle = color;
    ctx.fillText(label, mx, my);
  } else {
    ctx.save();
    ctx.translate(mx, my);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(-20, -7, 40, 14);
    ctx.fillStyle = color;
    ctx.fillText(label, 0, 0);
    ctx.restore();
  }

  ctx.restore();
}

// 검산식 캡션 (회색 소자): vertical=true면 -90도 회전
export function drawCalcCaption(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, vertical: boolean = false) {
  ctx.save();
  ctx.fillStyle = '#888'; ctx.font = '9px Arial';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  if (vertical) {
    ctx.translate(x, y); ctx.rotate(-Math.PI / 2); ctx.fillText(text, 0, 0);
  } else {
    ctx.fillText(text, x, y);
  }
  ctx.restore();
}

// 측면 음수 홈: 받아주는 패널의 옆면에 그리는 음수 (꺾임 결합 규칙 — 측면 음수는 항상 받는 쪽)
// dir: 홈이 파고드는 방향 ('right' = 왼쪽 면에서 오른쪽으로)
// 나중에 그린 패널 몸통에 가려진 양수 돌출 삼각형을 결합부 위에 다시 그림 (face = 패널 끝면 중앙, angle = 돌출 방향)
export function drawYangsuOverlay(ctx: CanvasRenderingContext2D, faceX: number, faceY: number, angle: number) {
  ctx.save();
  ctx.translate(faceX, faceY);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, -PT_PX / 2); ctx.lineTo(TD, 0); ctx.lineTo(0, PT_PX / 2);
  ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill();
  ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.restore();
}

export function drawSideEumsu(ctx: CanvasRenderingContext2D, faceX: number, faceY: number, dir: 'right' | 'left' | 'down' | 'up') {
  ctx.beginPath();
  if (dir === 'right') {
    ctx.moveTo(faceX, faceY - TD); ctx.lineTo(faceX + TD, faceY); ctx.lineTo(faceX, faceY + TD);
  } else if (dir === 'left') {
    ctx.moveTo(faceX, faceY - TD); ctx.lineTo(faceX - TD, faceY); ctx.lineTo(faceX, faceY + TD);
  } else if (dir === 'down') {
    ctx.moveTo(faceX - TD, faceY); ctx.lineTo(faceX, faceY + TD); ctx.lineTo(faceX + TD, faceY);
  } else {
    ctx.moveTo(faceX - TD, faceY); ctx.lineTo(faceX, faceY - TD); ctx.lineTo(faceX + TD, faceY);
  }
  ctx.closePath();
  ctx.fillStyle = PANEL_FILL; ctx.fill();
  ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.stroke();
}

// 코너형 하리 (모서리 기둥) — 메인 DrawingCanvas 확정 표현: 코너 사각형 + ㄱ자 연결 패널
export function drawCornerHari(
  ctx: CanvasRenderingContext2D,
  hari: Hari,
  bounds: { id: string; x: number; y: number; w: number; h: number; label: string; wallSide: string }[],
  cw: boolean = false,
) {
  const c = hari.corner!;
  const w = s(hari.width);
  const h = s(hari.height!);
  const x = (c === 'topLeft' || c === 'bottomLeft') ? PADDING : PADDING + s(H) - w;
  const y = (c === 'topLeft' || c === 'topRight') ? PADDING : PADDING + s(V) - h;
  const name = c === 'topLeft' ? '좌상' : c === 'topRight' ? '우상' : c === 'bottomLeft' ? '좌하' : '우하';

  ctx.save();
  ctx.strokeStyle = '#9c27b0'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
  ctx.fillStyle = '#9c27b0'; ctx.font = 'bold 11px Arial';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(`${name} 하리 (${hari.width}×${hari.height})`, x + w / 2, y + h / 2);
  ctx.restore();

  // 내벽 패널: 방쪽 ㄱ자 2변 (코너형은 외벽 없음)
  // 꺾임 규칙: 흐름(CW 시계/CCW 반시계) 순서 [들어오는 벽 → 내벽1 → 내벽2 → 나가는 벽]에서
  // 꺾이는 칸마다 "다음 패널"이 칸을 소유하고 코너로 시작, 앞 패널은 그 면에서 끝나며 양수로 꽂힘
  // → 내벽은 각각 (변 길이 − PT), 시작 코너 / 끝 양수 (6번). 3번(양수/양수) 생기지 않음
  const hw = hari.width, hh = hari.height!;
  type Arm = { sx: number; sy: number; dx: number; dy: number; len: number }; // 시작점(중심선, mm) + 흐름 방향
  const hArm = (sx: number, sy: number, dx: number): Arm => ({ sx, sy, dx, dy: 0, len: hw - PT });
  const vArm = (sx: number, sy: number, dy: number): Arm => ({ sx, sy, dx: 0, dy, len: hh - PT });
  const yTop = hh - PT / 2, yBot = V - hh + PT / 2;   // 수평 내벽 중심선
  const xLeft = hw - PT / 2, xRight = H - hw + PT / 2; // 수직 내벽 중심선
  // [내벽1, 내벽2, 들어오는 벽 흐름 방향]
  const FLOW: Record<string, [Arm, Arm, [number, number]]> = {
    'cw:topLeft':      [hArm(0, yTop, 1),        vArm(xLeft, hh, -1),      [0, -1]],
    'cw:topRight':     [vArm(xRight, 0, 1),      hArm(H - hw, yTop, 1),    [1, 0]],
    'cw:bottomRight':  [hArm(H, yBot, -1),       vArm(xRight, V - hh, 1),  [0, 1]],
    'cw:bottomLeft':   [vArm(xLeft, V, -1),      hArm(hw, yBot, -1),       [-1, 0]],
    'ccw:topLeft':     [vArm(xLeft, 0, 1),       hArm(hw, yTop, -1),       [-1, 0]],
    'ccw:topRight':    [hArm(H, yTop, -1),       vArm(xRight, hh, -1),     [0, -1]],
    'ccw:bottomRight': [vArm(xRight, V, -1),     hArm(H - hw, yBot, 1),    [1, 0]],
    'ccw:bottomLeft':  [hArm(0, yBot, 1),        vArm(xLeft, V - hh, 1),   [0, 1]],
  };
  const [arm1, arm2, [inX, inY]] = FLOW[`${cw ? 'cw' : 'ccw'}:${c}`];
  const ang = (dx: number, dy: number) => Math.atan2(dy, dx);
  // 코너 삼각형은 패널 로컬 +y 면을 밑변으로 그려짐 → 그 면이 들어오는 패널 쪽(−들어오는 방향)이어야
  // 들어오는 양수와 한 삼각형으로 겹침. 반대면 끝에서부터 거꾸로 그림 (타입·흐름은 동일: 시작 코너, 끝 양수)
  ([[arm1, inX, inY], [arm2, arm1.dx, arm1.dy]] as [Arm, number, number][]).forEach(([a, ix, iy]) => {
    const t = ang(a.dx, a.dy);
    const flip = Math.round(-Math.sin(t)) !== -ix || Math.round(Math.cos(t)) !== -iy;
    if (!flip) {
      drawHariPanelLine(ctx, a.len, PADDING + s(a.sx), PADDING + s(a.sy), t, 'corner', 'yangsu', 'yangsu');
    } else {
      drawHariPanelLine(ctx, a.len, PADDING + s(a.sx + a.dx * a.len), PADDING + s(a.sy + a.dy * a.len), t + Math.PI, 'yangsu', 'corner', 'eumsu');
    }
  });
  // 먼저 그린 패널의 양수가 나중 패널 몸통에 가려지므로 결합부 위에 다시 그림
  // 들어오는 벽 끝(4mm 간격) → 내벽1 시작 칸 / 내벽1 끝 → 내벽2 시작 칸
  const c1x = arm1.sx + arm1.dx * PT / 2, c1y = arm1.sy + arm1.dy * PT / 2;
  drawYangsuOverlay(ctx, PADDING + s(c1x - inX * (PT / 2 + 4)), PADDING + s(c1y - inY * (PT / 2 + 4)), ang(inX, inY));
  const c2x = arm2.sx + arm2.dx * PT / 2, c2y = arm2.sy + arm2.dy * PT / 2;
  drawYangsuOverlay(ctx, PADDING + s(c2x - arm1.dx * PT / 2), PADDING + s(c2y - arm1.dy * PT / 2), ang(arm1.dx, arm1.dy));

  bounds.push({ id: hari.id, x, y, w, h, label: name, wallSide: 'corner' });
}

export function splitPanels(lengthMM: number): number[] {
  const max = CONFIG.DEFAULT_SIZE; // 표준 폭
  const halfSize = HALF_SIZE;
  const minSize = CONFIG.SPLIT_MIN_SIZE!;
  const space = CONFIG.PANEL_SPACE; // 4
  if (lengthMM <= 0) return [];
  if (lengthMM <= max) return [lengthMM];
  const sizes: number[] = [];
  let remaining = lengthMM;
  while (remaining > max + space) {
    sizes.push(max);
    remaining -= max + space;
  }
  remaining -= space;
  if (remaining >= minSize) {
    sizes.push(remaining);
    return sizes;
  }
  // 200 미만 → 마지막 900을 441로 교체
  if (sizes.length > 0) {
    sizes[sizes.length - 1] = halfSize;
    remaining += (max - halfSize);
    if (remaining > max) {
      sizes.push(max);
      remaining -= max + space;
    }
    if (remaining > 0) sizes.push(remaining);
  } else {
    if (remaining > 0) sizes.push(remaining);
  }
  return sizes;
}

/**
 * 하리 내벽 줄 사이즈 수정 → 줄 길이 유지
 * 흡수 = 줄 끝(그리기 순서 마지막)부터 수정 안 된 첫 패널, 없으면 남는 길이로 패널 추가
 * 접합 간격: 분할 규칙대로 패널 수 × 4 (1장일 땐 줄 길이 그대로)
 */
export function rebalanceLine(length: number, base: number[], ov: (number | undefined)[]): { sizes: number[]; error: string | null } {
  const edited = base.map((b, i) => ov[i] !== undefined && ov[i] !== b);
  if (!edited.some(Boolean)) return { sizes: base, error: null };
  const sizes = base.map((b, i) => (edited[i] ? ov[i]! : b));
  const sp = CONFIG.PANEL_SPACE;
  let absorber = -1;
  for (let i = sizes.length - 1; i >= 0; i--) if (!edited[i]) { absorber = i; break; }
  if (absorber < 0) { // 흡수할 패널 없음 → 새 패널
    sizes.push(0);
    absorber = sizes.length - 1;
  }
  const others = sizes.reduce((a, v, i) => (i === absorber ? a : a + v), 0);
  const rest = length - others - (sizes.length > 1 ? sp * sizes.length : 0);
  if (rest < PANEL_MIN || rest > PANEL_MAX) {
    return { sizes: base, error: `나머지 패널이 ${rest}mm가 됨 (허용 ${PANEL_MIN}~${PANEL_MAX})` };
  }
  sizes[absorber] = rest;
  return { sizes, error: null };
}

export function drawHariPanelLine(
  ctx: CanvasRenderingContext2D,
  lengthMM: number,
  baseX: number, baseY: number,
  angle: number,
  startLT: string, endRT: string,
  startRT?: string,
) {
  const base = splitPanels(lengthMM);
  const startNum = panelCounter + 1;
  if (hariLineRec) base.forEach((_, i) => { hariLineRec![startNum + i] = { length: lengthMM, startNum, base }; });
  const sizes = rebalanceLine(lengthMM, base, base.map((_, i) => currentOverrides[startNum + i]?.size)).sizes;
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const space = s(CONFIG.PANEL_SPACE);
  let ltX = baseX, ltY = baseY;

  let prevRt = '';
  sizes.forEach((size, i) => {
    const pl = s(size);
    let lt = i === 0 ? startLT : (prevRt === 'yangsu' ? 'eumsu' : 'yangsu');
    let rt = i === sizes.length - 1 ? endRT : 'eumsu';
    if (i === 0 && sizes.length > 1 && startRT !== undefined) rt = startRT;
    prevRt = rt;
    drawPanel(ctx, ltX + dx * pl / 2, ltY + dy * pl / 2,
      pl, PT_PX, angle, lt, rt, `${size}`);
    ltX += dx * (pl + space);
    ltY += dy * (pl + space);
  });
}

// ── 결합부 검사: 패널 끝 좌표 → 가장 가까운 상대 패널 끝 (40px 이내) ──
export function panelEnd(p: DrawnPanel, side: 'lt' | 'rt') {
  const sg = side === 'lt' ? -1 : 1;
  return { x: p.cx + sg * (p.pl / 2) * Math.cos(p.angle), y: p.cy + sg * (p.pl / 2) * Math.sin(p.angle) };
}
export function findPartnerEnd(panels: DrawnPanel[], me: DrawnPanel, side: 'lt' | 'rt') {
  const e = panelEnd(me, side);
  let bd = Infinity, bp: DrawnPanel | null = null, bs: 'lt' | 'rt' = 'lt';
  for (const p of panels) {
    if (p.num === me.num) continue;
    for (const s of ['lt', 'rt'] as const) {
      const q = panelEnd(p, s);
      const d = Math.hypot(e.x - q.x, e.y - q.y);
      if (d < bd) { bd = d; bp = p; bs = s; }
    }
  }
  return bp && bd < 40 ? { p: bp, s: bs, type: bs === 'lt' ? bp.lt : bp.rt } : null;
}
// 코너는 양수만 받을 수 있음 — 코너+음수 조합은 결합 불가
export const isCornerEumsu = (a: string, b: string) =>
  (a === 'corner' && b === 'eumsu') || (a === 'eumsu' && b === 'corner');

// 결합 불가 결합부를 빨간 원으로 표시
export function markInvalidJoints(ctx: CanvasRenderingContext2D, panels: DrawnPanel[]) {
  const done = new Set<string>();
  panels.forEach(me => (['lt', 'rt'] as const).forEach(side => {
    const partner = findPartnerEnd(panels, me, side);
    if (!partner || !isCornerEumsu(side === 'lt' ? me.lt : me.rt, partner.type)) return;
    const key = [`${me.num}${side}`, `${partner.p.num}${partner.s}`].sort().join('|');
    if (done.has(key)) return;
    done.add(key);
    const a = panelEnd(me, side), b = panelEnd(partner.p, partner.s);
    ctx.save();
    ctx.strokeStyle = '#e53935'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc((a.x + b.x) / 2, (a.y + b.y) / 2, PT_PX * 1.2, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }));
}

// 제작 가능 패널 크기 — 재질 규격표 (최대 = 표준 폭, 최소 = 분할 최소 조각)
export const PANEL_MIN = getMaterialSpec(MATERIAL).minWidth;
export const PANEL_MAX = getMaterialSpec(MATERIAL).standardWidth;

export type Overrides = Record<number, { lt?: string; rt?: string; size?: number }>;
export type WallSet = { top: Panel[]; right: Panel[]; bottom: Panel[]; left: Panel[] };

// CW 그리기 순서 기준 벽 패널 번호: 상단(→) → 우측(↓) → 하단(역순) → 좌측(역순)
export function cwWallNums(w: WallSet) {
  const T = w.top.length, R = w.right.length, B = w.bottom.length, L = w.left.length;
  return {
    top: w.top.map((_, i) => i + 1),
    right: w.right.map((_, i) => T + i + 1),
    bottom: w.bottom.map((_, i) => T + R + (B - 1 - i) + 1),
    left: w.left.map((_, i) => T + R + B + (L - 1 - i) + 1),
  };
}

/**
 * 벽 패널 사이즈 변경 → 같은 구간(코너~하리 사이 연속 배치) 합계 유지
 * 흡수 패널 = 구간 끝(배치 순서 마지막: 가로 오른쪽, 세로 아래쪽)부터 수정 안 된 첫 패널
 * 구간 밖(코너 너머 다른 벽)으로는 전달하지 않음. 위치는 구간 시작부터 다시 쌓음.
 */
export const FILL_SPEC: FillSpec = { min: PANEL_MIN, max: PANEL_MAX, standard: CONFIG.DEFAULT_SIZE, half: HALF_SIZE, space: CONFIG.PANEL_SPACE };
export const SIDE_NAME: Record<keyof WallSet, string> = { top: '상단벽', right: '우측벽', bottom: '하단벽', left: '좌측벽' };

// 벽 구간 분리: 연속 배치(이전 끝 + 간격 = 다음 시작)끼리 한 구간. key = 벽:구간 시작 위치 (규칙 적용과 무관하게 불변)
export type Segment = { side: keyof WallSet; key: string; idxs: number[]; start: number; total: number };
export function wallSegments(w: WallSet): Segment[] {
  const sp = CONFIG.PANEL_SPACE;
  const out: Segment[] = [];
  (['top', 'right', 'bottom', 'left'] as const).forEach(side => {
    const isV = side === 'left' || side === 'right';
    const pos = (p: Panel) => (isV ? p.y : p.x);
    const panels = w[side];
    panels.forEach((p, i) => {
      const prev = panels[i - 1];
      const cur = out[out.length - 1];
      if (prev && cur && cur.side === side && Math.abs(pos(p) - (pos(prev) + prev.size + sp)) < 1) cur.idxs.push(i);
      else out.push({ side, key: `${side}:${Math.round(pos(p))}`, idxs: [i], start: pos(p), total: 0 });
    });
  });
  out.forEach(sg => {
    const last = w[sg.side][sg.idxs[sg.idxs.length - 1]];
    const isV = sg.side === 'left' || sg.side === 'right';
    sg.total = Math.round((isV ? last.y : last.x) + last.size + sp - sg.start);
  });
  return out;
}

/** 구간 채우기 규칙 적용 → 구간 패널을 규칙 결과로 교체 (패널 수가 바뀔 수 있음) */
export function applySegRules(w: WallSet, rules: Record<string, SegRule>): WallSet {
  const out = { top: [...w.top], right: [...w.right], bottom: [...w.bottom], left: [...w.left] } as WallSet;
  const sp = CONFIG.PANEL_SPACE;
  // 뒤 구간부터 교체해야 앞 구간 인덱스가 유지됨
  wallSegments(w).reverse().forEach(sg => {
    const rule = rules[sg.key];
    if (!rule) return;
    const r = fillSegment(sg.total, rule, FILL_SPEC);
    if (r.error) return;
    const isV = sg.side === 'left' || sg.side === 'right';
    const first = w[sg.side][sg.idxs[0]], last = w[sg.side][sg.idxs[sg.idxs.length - 1]];
    let cur = sg.start;
    const panels: Panel[] = r.sizes.map((size, k) => {
      const n = r.sizes.length;
      // 끝 타입은 원래 구간 양끝 것을 이어받고, 중간은 기본 1번(양수/음수)
      const leftType = k === 0 ? first.leftType : 'yangsu';
      const rightType = k === n - 1 ? last.rightType : 'eumsu';
      const p: Panel = { ...first, size, leftType, rightType, isCustom: size !== CONFIG.DEFAULT_SIZE,
        x: isV ? 0 : cur, y: isV ? cur : 0, width: isV ? 0 : size, height: isV ? size : 0 };
      cur += size + sp;
      return p;
    });
    out[sg.side].splice(sg.idxs[0], sg.idxs.length, ...panels);
  });
  return out;
}

export function rebalanceWalls(w: WallSet, ov: Overrides): { walls: WallSet; errors: Record<number, string> } {
  const nums = cwWallNums(w);
  const errors: Record<number, string> = {};
  const sp = CONFIG.PANEL_SPACE;
  const out = {} as WallSet;
  (['top', 'right', 'bottom', 'left'] as const).forEach(side => {
    const panels = w[side].map(p => ({ ...p }));
    const isV = side === 'left' || side === 'right';
    const pos = (p: Panel) => (isV ? p.y : p.x);
    const segs: number[][] = [];
    panels.forEach((p, i) => {
      const prev = panels[i - 1];
      if (prev && Math.abs(pos(p) - (pos(prev) + prev.size + sp)) < 1) segs[segs.length - 1].push(i);
      else segs.push([i]);
    });
    segs.forEach(seg => {
      const newSize = (i: number) => ov[nums[side][i]]?.size;
      const edited = seg.filter(i => newSize(i) !== undefined && newSize(i) !== panels[i].size);
      if (edited.length === 0) return;
      const absorber = [...seg].reverse().find(i => !edited.includes(i));
      if (absorber === undefined) {
        edited.forEach(i => { errors[nums[side][i]] = '같은 구간에 길이를 맞출 패널이 없음'; });
        return;
      }
      const total = seg.reduce((a, i) => a + panels[i].size, 0);
      const others = seg.filter(i => i !== absorber).reduce((a, i) => a + (newSize(i) ?? panels[i].size), 0);
      const absSize = total - others;
      if (absSize > PANEL_MAX || absSize < PANEL_MIN) {
        edited.forEach(i => { errors[nums[side][i]] = `끝 패널이 ${absSize}mm가 됨 (허용 ${PANEL_MIN}~${PANEL_MAX})`; });
        return;
      }
      edited.forEach(i => { panels[i].size = newSize(i)!; });
      panels[absorber].size = absSize;
      let cur = pos(panels[seg[0]]);
      seg.forEach(i => {
        const p = panels[i];
        if (isV) { p.y = cur; p.height = p.size; } else { p.x = cur; p.width = p.size; }
        p.isCustom = p.size !== CONFIG.DEFAULT_SIZE;
        cur += p.size + sp;
      });
    });
    out[side] = panels;
  });
  return { walls: out, errors };
}

// CW 벽 레이아웃 (좌측 하리 있으면 좌측벽이 좌하단 코너 셀까지 관통 → 분할 길이에 PT 포함)
export function cwLayout(haris: Hari[]): WallSet {
  const hariMiddle = haris.find(h => h.wallSide === 'left');
  const hasBL = haris.some(h => h.hariType === 'corner' && h.corner === 'bottomLeft');
  // 코너 하리 제외 폭 보정 — 벽이 꺾임 규칙상 끝/시작 위치에 오도록 (벽마다 그리기 시작점이 달라 보정량이 다름)
  // 상단: 우상은 수직 바깥면(4000)까지 / 우측: 우상 칸 소유(900부터) / 하단: 우하 칸 소유(4100까지)
  // 좌측: 좌상은 수평 아랫면(1000)부터, 좌하 칸 소유(4100까지)
  return calculateAllWalls(H, V, CONFIG, [], PT, haris, hariMiddle && !hasBL ? PT : 0, {
    'top:topRight': -PT,
    'right:topRight': -PT,
    'bottom:bottomRight': -PT,
    'left:topLeft': -PT,
    'left:bottomLeft': hariMiddle ? -PT : 0,
  });
}
export const panelSizeError = (v: string) => {
  const n = parseInt(v);
  if (isNaN(n)) return '숫자를 입력하세요';
  if (n > PANEL_MAX) return `최대 ${PANEL_MAX}mm — 제작 불가`;
  if (n < PANEL_MIN) return `최소 ${PANEL_MIN}mm`;
  return null;
};


// ══════════════════════════════════════════
// 장면 그리기 (CW/CCW) — 캔버스에 그리고 그려진 패널 목록 반환
// ══════════════════════════════════════════
export type HariBound = { id: string; x: number; y: number; w: number; h: number; label: string; wallSide: string };
export interface SceneParams {
  haris: Hari[];
  overrides: Overrides;              // 패널별 수정값 (CW만 적용)
  segRules: Record<string, SegRule>; // 구간 채우기 규칙 (CW만 적용)
  bounds: HariBound[];               // 하리 클릭 영역 (출력)
}

/** 도면 치수·배율·여백 설정 (그리기 전에 호출) */
export function configureEngine(opts: { H?: number; V?: number; PT?: number; scale?: number; padding?: number }) {
  if (opts.H !== undefined) H = opts.H;
  if (opts.V !== undefined) V = opts.V;
  if (opts.PT !== undefined) PT = opts.PT;
  if (opts.scale !== undefined) SCALE = opts.scale;
  if (opts.padding !== undefined) PADDING = opts.padding;
  PT_PX = s(PT);
  TD = PT_PX * 0.5;
}
export const getEngineDims = () => ({ H, V, PT, SCALE, PADDING });
/** 캔버스 크기 (도면 + 치수선 여백) */
export const sceneSize = () => ({ W: PADDING * 2 + s(H) + 120, HH: PADDING * 2 + s(V) + 180 });
/** 클릭 판정 등에 쓰는 "현재 패널 목록"을 지정 (CW/CCW 두 장면을 그린 뒤 CW 목록으로 되돌릴 때) */
export function setActivePanels(panels: DrawnPanel[], overrides: Overrides = {}) {
  drawnPanels = panels;
  currentOverrides = overrides;
}

export function drawSceneCW(ctx: CanvasRenderingContext2D, p: SceneParams): DrawnPanel[] {
  const haris = p.haris;
    panelCounter = 0;
    hariLineRec = cwHariLines = {};
    drawnPanels = [];
    currentOverrides = p.overrides;
    currentRules = p.segRules;

    const W = PADDING * 2 + s(H) + 120;
    const HH = PADDING * 2 + s(V) + 180;

    ctx.fillStyle = '#f5f5f5'; ctx.fillRect(0, 0, W, HH);
    ctx.fillStyle = '#fff'; ctx.fillRect(PADDING, PADDING, s(H), s(V));

    const hariMiddle = haris.find(h => h.wallSide === 'left');
    const hariBottom = haris.find(h => h.wallSide === 'bottom');
    const hariRight = haris.find(h => h.wallSide === 'right');
    const hariTop = haris.find(h => h.wallSide === 'top');
    const allHaris = haris;
    const hariMiddlePos = hariMiddle ? hariMiddle.position! : Number.POSITIVE_INFINITY;
    const hariBottomPos = hariBottom ? hariBottom.position! : Number.POSITIVE_INFINITY;
    const hariRightPos = hariRight ? hariRight.position! : Number.POSITIVE_INFINITY;
    const hariTopPos = hariTop ? hariTop.position! : Number.POSITIVE_INFINITY;
    const cornTL = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'topLeft');
    const cornTR = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'topRight');
    const cornBL = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'bottomLeft');
    const cornBR = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'bottomRight');
    const hasTL = !!cornTL, hasTR = !!cornTR, hasBL = !!cornBL, hasBR = !!cornBR;
    // 좌측 하리 있을 때 좌측벽 맨 아래 패널이 좌하단 코너 셀까지 관통 → 분할 길이에 PT 포함 (1800 → 900+892)
    const leftOwnsBL = !!hariMiddle && !hasBL;
    // 사이즈 수정 반영: 같은 구간 끝 패널이 차이를 흡수 (rebalanceWalls)
    const walls = rebalanceWalls(applySegRules(cwLayout(allHaris), currentRules), currentOverrides).walls;

    // ══════════════════════════════════════════
    // 시계방향(↻) 벽 패널: 상단(→) → 우측(↓) → 하단(←,역순) → 좌측(↑,역순)
    // ══════════════════════════════════════════

    // ── 1. 상단벽 (→, 좌→우) — lt/rt swap하여 화살표 시계방향
    const lastTopIdx = walls.top.length - 1;
    const hariTopRightX = hariTopPos + (hariTop?.width ?? 0);
    const topPanelSizesLeft: string[] = [];
    const topPanelSizesRight: string[] = [];
    // #1의 corner 상태 확인 (override로 양수 변경되었는지)
    const firstTopPanelNum = panelCounter + 1;
    const firstTopHasCorner = !currentOverrides[firstTopPanelNum]?.lt
      || currentOverrides[firstTopPanelNum]?.lt === 'corner';
    walls.top.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === lastTopIdx;
      let pl = s(p.size);
      let eSize = p.size; // 렌더링 크기(mm) — 라벨용
      let cx = PADDING + s(PT + p.x) + s(p.size) / 2;
      let lt = isFirst ? 'corner' : (p.leftType === 'yangsu' ? 'eumsu' : p.leftType === 'eumsu' ? 'yangsu' : (p.leftType || 'eumsu'));
      let rt = isLast ? 'yangsu' : (p.rightType === 'eumsu' ? 'yangsu' : p.rightType === 'yangsu' ? 'eumsu' : (p.rightType || 'yangsu'));
      // isLast: 실제 크기 렌더 (우상단 코너는 우측벽이 소유 — 우측벽 내면 4900에서 끝)
      const isBeforeHari = Math.abs((p.x + p.size) - hariTopPos) < 50;
      if (isBeforeHari) {
        rt = 'yangsu';
        if (isLast || firstTopHasCorner) {
          // 실제 크기 — 왼팔이 상단벽 밴드를 관통하므로 팔 앞(−4)에서 끝
          pl = s(p.size); eSize = p.size;
          cx = PADDING + s(PT + p.x) + s(p.size) / 2;
        } else {
          pl = s(p.size - 4); eSize = p.size - 4;
          cx = PADDING + s(PT + p.x) + s(p.size - 4) / 2;
        }
      }
      const isAfterHari = Math.abs((PT + p.x) - hariTopRightX) < 200;
      if (isAfterHari) {
        lt = 'corner'; rt = 'yangsu';
        // 구간이 팔 두께를 포함하므로 확장 없이 실제 크기로 렌더 (900 안에 팔 100 포함)
        pl = s(p.size); eSize = p.size;
        cx = PADDING + s(PT + p.x) + s(p.size) / 2;
      }
      const capTerm = `${p.size}`; // 검산식 항 = 주문 크기 (확장분은 숫자에 포함된 것으로 간주)
      if (isAfterHari || (p.x >= hariTopPos)) {
        topPanelSizesRight.push(capTerm);
        // 오른쪽 구간: 오른팔(벽 내면에서 시작)을 덮도록 팔 바깥면 위치로 −PT
        cx -= PT_PX;
      } else {
        topPanelSizesLeft.push(capTerm);
        if (firstTopHasCorner) cx -= PT_PX; // 좌상 칸(외곽 코너 또는 코너 하리 칸) 소유 → x=0 기준
      }
      const topLabel = p.size; // 라벨 = 주문 크기 (최대 900), 확장분은 캡션 검산식에 표시
      drawPanel(ctx, cx, PADDING + PT_PX / 2, pl, PT_PX, 0, lt, rt, `${topLabel}`);
    });

    // ── 2. 우측벽 (↓, 위→아래) — lt/rt swap
    const lastRightIdx = walls.right.length - 1;
    const hariRightTopY = hariRightPos;
    const hariRightBottomY = hariRightPos + (hariRight?.width ?? 0);
    const rWallCapAbove: string[] = [];
    const rWallCapBelow: string[] = [];
    walls.right.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === lastRightIdx;
      let pl = s(p.size);
      let cy = PADDING + s(p.y) + s(p.size) / 2;
      let eSize = p.size;
      let lt = isFirst ? 'corner' : (p.leftType === 'yangsu' ? 'eumsu' : p.leftType === 'eumsu' ? 'yangsu' : (p.leftType || 'eumsu'));
      let rt = isLast ? 'yangsu' : (p.rightType === 'eumsu' ? 'yangsu' : p.rightType === 'yangsu' ? 'eumsu' : (p.rightType || 'yangsu'));
      if (isLast) {
        lt = 'eumsu'; rt = 'yangsu';
        // 하단벽이 [4900~5000] 명목 위치라 실제 크기로 렌더 — 양수가 벽 몸통에 꽂힘
      }
      const isBeforeHariRight = Math.abs((p.y + p.size) - hariRightTopY) < 200;
      if (isBeforeHariRight) {
        rt = 'yangsu';
        if (!isFirst) { lt = 'eumsu'; }
        eSize = p.size - 4;
      }
      const isAfterHariRight = Math.abs(p.y - hariRightBottomY) < 200;
      if (p.y >= hariRightBottomY - 200) {
        // 구간이 팔 두께를 포함하므로 위로 올림 없이 실제 위치 (900 안에 팔 100 포함)
        if (isAfterHariRight) {
          lt = 'corner'; rt = 'yangsu';
          eSize = p.size - 4;
        }
      }
      const rLabel = p.size;
      (p.y < hariRightTopY ? rWallCapAbove : rWallCapBelow).push(`${rLabel}`);
      drawPanel(ctx, PADDING + s(H - PT) + PT_PX / 2, cy, pl, PT_PX, -Math.PI * 1.5, lt, rt, `${rLabel}`);
    });

    // ── 3. 하단벽 (←, 우→좌 역순) — lt/rt swap
    const hariBottomRightX = hariBottomPos + (hariBottom?.width ?? 0);
    const hariBottomLeftX = hariBottomPos;
    const bottomReversed = [...walls.bottom].reverse();
    const lastBottomIdx = bottomReversed.length - 1;
    const bWallCapLeft: string[] = [];
    const bWallCapRight: string[] = [];
    bottomReversed.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === lastBottomIdx;
      let pl = s(p.size);
      let eSize = p.size;
      let cx = PADDING + s(PT + p.x) + s(p.size) / 2; // 상단벽과 동일 기준
      const cy = PADDING + s(V - PT) + PT_PX / 2; // 명목 위치 [4900~5000] — 외곽선 밀착
      let lt = isFirst ? 'corner' : 'eumsu';
      let rt = 'yangsu';
      if (isLast) {
        lt = 'eumsu'; rt = 'yangsu';
      }
      // isFirst(우측 끝): 코너 패널이 우측벽 기둥까지 관통(코너 소유) — 분할 구간이 외곽 5000까지라 확장 불필요
      // 하리 오른쪽 구간(우측 끝 제외): 정위치 정렬 — 추가 이동 없음 (팔 앞 4mm에서 시작)
      if (p.x >= hariBottomRightX - PT - 10 && !isFirst) {
        lt = 'eumsu'; rt = 'yangsu';
      }
      let bAngle = -Math.PI;
      if (hariBottom && p.x + p.size < hariBottomLeftX - 200 && !isLast) {
        lt = 'yangsu'; rt = 'eumsu'; bAngle = 0;
      }
      const isHariLeftAdj = Math.abs((p.x + p.size) - hariBottomLeftX) < 200;
      if (isHariLeftAdj) {
        lt = 'corner'; rt = 'yangsu'; // 실제 크기로 왼팔 칸을 덮음 (…+647+4 = 개구부 2100)
      }
      const bLabel = p.size; // 라벨 = 주문 크기 (우측 끝 892는 우측벽 두께 포함)
      (p.x < hariBottomLeftX ? bWallCapLeft : bWallCapRight).unshift(`${bLabel}`);
      drawPanel(ctx, cx, cy, pl, PT_PX, bAngle, lt, rt, `${bLabel}`);
    });

    // ── 4. 좌측벽 (↑, 아래→위 역순) — lt/rt swap
    // 하리 아래 첫 패널 인덱스 (동적 계산 — 하리 이동에 대응)
    const hariEndY = hariMiddlePos + (hariMiddle?.width ?? 0);
    const hariBottomIdx = walls.left.findIndex(p => p.y >= hariEndY - PT - 1);
    const leftReversed = [...walls.left].reverse();
    const lastLeftIdx = leftReversed.length - 1;
    const leftStartCounter = panelCounter; // drawPanel 호출 전 캡처
    const lWallCapAbove: string[] = [];
    const lWallCapBelow: string[] = [];
    leftReversed.forEach((p, ri) => {
      const origIdx = walls.left.length - 1 - ri;
      const isFirstCW = ri === 0; // CW 첫 = 맨 아래
      const isLastCW = ri === lastLeftIdx; // CW 마지막 = 맨 위
      // size override 확인 (effectiveSize로 모든 조건에서 사용)
      const panelNum = leftStartCounter + ri + 1;
      const sizeOvr = currentOverrides[panelNum]?.size;
      const sz = sizeOvr || p.size; // effective size
      let pl = s(sz);
      let eSize = sz;
      let cy = PADDING + s(p.y) + s(sz) / 2;
      let lt = isFirstCW ? 'corner' : 'yangsu';
      let rt = isFirstCW ? 'yangsu' : 'eumsu';
      let angle = -Math.PI * 1.5;
      if (isFirstCW) {
        angle = -Math.PI / 2;
        // 좌하단 코너 소유: leftOwnsBL이면 분할이 이미 코너 셀 포함 (하리 없으면 전체 내림이 대신함)
        // 좌하 코너 하리면 제외 폭 보정으로 하리 칸(4100)에서 끝 — 확장 없음
      }
      // 하리 위 패널: 두께만큼 아래로 이동 (벽 두께 영역) — 하리 없으면 전체 적용
      // (좌상 코너 하리는 제외 폭 보정으로 수평 내벽 아랫면에서 시작)
      if (!hariMiddle || origIdx < hariBottomIdx) {
        cy += PT_PX;
      }
      // 하리 아래 첫 패널: 팔이 벽 기둥을 관통하며 코너 소유 → 벽은 1번(양수) 유지
      if (origIdx === hariBottomIdx - 1) {
        // 하리 위 마지막 패널: +PT 이동 위치에서 위팔 칸까지 실제 크기로 덮음 (…+647+4 = 2100)
        lt = 'corner'; rt = 'yangsu'; angle = -Math.PI / 2;
      }
      if (origIdx === hariBottomIdx && isFirstCW) {
        // 하리 아래 패널이 하나뿐: 아래끝 코너(좌하 칸), 위끝 양수가 관통한 아래팔 몸통에 꽂힘 — 실제 크기
        lt = 'corner'; rt = 'yangsu'; angle = -Math.PI / 2;
      }
      if (isLastCW && origIdx !== hariBottomIdx - 1) {
        const lastOvr = currentOverrides[leftStartCounter + ri + 1];
        if (lastOvr?.rt === 'corner') {
          lt = 'eumsu'; rt = 'corner'; angle = -Math.PI / 2;
        } else {
          lt = 'yangsu'; rt = 'eumsu'; angle = -Math.PI * 1.5;
          // PT 축소 제거 — 실제 크기로 렌더링
        }
      }
      const isHariAdjacentLeft = (origIdx === hariBottomIdx - 1) || (origIdx === hariBottomIdx);
      const lLabel = sz;
      ((!hariMiddle || origIdx < hariBottomIdx) ? lWallCapAbove : lWallCapBelow).unshift(`${lLabel}`);
      drawPanel(ctx, PADDING + PT_PX / 2, cy, pl, PT_PX, angle, lt, rt, `${lLabel}`);
    });

    // ══════════════════════════════════════════
    // 시계방향(↻) 하리 내벽 패널
    // CCW: 1→2→3 순서를 CW: 3→2→1 (역순) + 방향 반전
    // ══════════════════════════════════════════

    if (hariMiddle) {
    // ── 좌측 하리 (뒤집힌 ㄷ, 왼쪽 벽으로 열림)
    // CCW: 하단H(→) → 우측V(↓) → 상단H(←)
    // CW:  상단H(→) → 우측V(↑) → 하단H(←)
    const hx = PADDING;
    const hy = PADDING + s(hariMiddlePos);
    const hw = s(hariMiddle.height);
    const hh = s(hariMiddle.width);

    ctx.save();
    ctx.strokeStyle = '#e53935'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(hx, hy, hw + PT_PX, hh);
    ctx.setLineDash([]); ctx.restore();
    p.bounds.push({ id: hariMiddle.id, x: hx, y: hy, w: hw + PT_PX, h: hh, label: '좌측', wallSide: 'left' });

    ctx.save();
    ctx.fillStyle = '#e53935'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(`좌측 하리 CW (${hariMiddle.width - PT * 2}×${hariMiddle.height})`, hx + hw / 2, hy - 4);
    ctx.restore();

    drawDimension(ctx, hx, hy + hh, hx + hw, hy + hh, `${hariMiddle.height}`, 45, '#e53935');
    drawDimension(ctx, hx + hw + PT_PX, hy + PT_PX, hx + hw + PT_PX, hy + hh - PT_PX, `${hariMiddle.width - PT * 2}`, 45, '#e53935');
    // 상단벽~하리내벽, 하리내벽~하단벽 거리 (바깥쪽, 하리 두께 포함)
    drawDimension(ctx, PADDING, PADDING, PADDING, hy + PT_PX, `${hariMiddlePos + PT}`, -40, '#e53935');
    // 하리 폭 (내부)
    drawDimension(ctx, PADDING, hy + PT_PX, PADDING, hy + hh - PT_PX, `${hariMiddle.width - PT * 2}`, -40, '#e53935');
    drawDimension(ctx, PADDING, hy + hh - PT_PX, PADDING, PADDING + s(V), `${V - hariMiddlePos - hariMiddle.width + PT}`, -40, '#e53935');

    // 좌측벽 구간 검산식 캡션
    drawCalcCaption(ctx, `(${cornTL ? cornTL.height + '+' : ''}${lWallCapAbove.join('+4+')}+4+100 = ${hariMiddlePos + PT})`, 8, PADDING + s((hariMiddlePos + PT) / 2), true);
    drawCalcCaption(ctx, `(100+${lWallCapBelow.join('+4+')}+4${cornBL ? '+' + cornBL.height : ''} =${V - hariMiddlePos - hariMiddle.width + PT})`, 8, PADDING + s((hariMiddlePos + hariMiddle.width - PT + V) / 2), true);

    const hLen = hariMiddle.height;
    const vLen = hariMiddle.width - PT * 2;

    // CW 1: 상단H (→, 0°): none→corner
    drawHariPanelLine(ctx, hLen, hx + PT_PX, hy + PT_PX / 2, 0, 'yangsu', 'corner');

    // CW 2: 우측V (↑) 수동 분할 — 첫 패널 4번(yangsu/corner), 나머지 8번(eumsu/yangsu)
    const hCW2Sizes = splitPanels(vLen + PT);
    let hCW2Y = hy + hh;
    hCW2Sizes.forEach((size, i) => {
      const pl = s(size);
      const isFirst = i === 0;
      const lt = isFirst ? 'yangsu' : 'eumsu';
      const rt = isFirst ? 'corner' : 'yangsu';
      const angle = isFirst ? -Math.PI * 1.5 : -Math.PI / 2;
      hCW2Y -= pl;
      drawPanel(ctx, hx + hw + PT_PX / 2, hCW2Y + pl / 2, pl, PT_PX, angle, lt, rt, `${size}`);
      hCW2Y -= s(CONFIG.PANEL_SPACE);
    });

    // CW 3: 하단H 수동 분할 — 팔이 벽 기둥 관통, 벽쪽 끝(isLast)이 코너 소유
    const hCW3Sizes = splitPanels(hLen);
    let hCW3X = hx + hw;
    hCW3Sizes.forEach((size, i) => {
      const pl = s(size);
      const isLast = i === hCW3Sizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const angle = isLast ? 0 : -Math.PI;
      drawPanel(ctx, hCW3X - pl / 2, hy + hh - PT_PX / 2, pl, PT_PX, angle, lt, rt, `${size}`);
      hCW3X -= pl + s(CONFIG.PANEL_SPACE);
    });

    } else {
      // 좌측 하리 없음: 전체 세로 치수 + 검산식 (패널이 +PT 내려가므로 상단벽 100 별도)
      drawDimension(ctx, PADDING, PADDING, PADDING, PADDING + s(V), `${V}`, -40, '#e53935');
      if (!hasTL && !hasBL) drawCalcCaption(ctx, `(100+${lWallCapAbove.join('+4+')}+4 = ${V})`, 8, PADDING + s(V) / 2, true);
    }

    if (hariBottom) {
    // ── 하단 하리 (ㄷ, 아래쪽 벽으로 열림)
    // CCW: 우측V(↑) → 상단H(→) → 좌측V(↓)
    // CW:  좌측V(↑) → 상단H(←) → 우측V(↓)
    const bx = PADDING + s(hariBottomPos); // 명목 위치 [position, position+width]
    const by = PADDING + s(V) - s(hariBottom.height!); // 명목 위치 — 내려간 하단벽 위에 얹힘
    const bw = s(hariBottom.width);
    const bh = s(hariBottom.height!);

    ctx.save();
    ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(bx, by - PT_PX, bw, bh + PT_PX);
    ctx.setLineDash([]); ctx.restore();
    p.bounds.push({ id: hariBottom.id, x: bx, y: by - PT_PX, w: bw, h: bh + PT_PX, label: '하단', wallSide: 'bottom' });

    ctx.save();
    ctx.fillStyle = '#1565c0'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(`하단 하리 CW (${hariBottom.width - PT * 2}×${hariBottom.height!})`, bx + bw / 2, by - 4);
    ctx.restore();

    drawDimension(ctx, bx + PT_PX, by - PT_PX, bx + bw - PT_PX, by - PT_PX, `${hariBottom.width - PT * 2}`, -45, '#1565c0');
    drawDimension(ctx, bx, by, bx, by + bh, `${hariBottom.height!}`, -45, '#1565c0');
    // 좌측벽~하리내벽, 하리내벽~우측벽 거리 (바깥쪽, 하리 두께 포함)
    drawDimension(ctx, PADDING, PADDING + s(V), bx + PT_PX, PADDING + s(V), `${hariBottomPos + PT}`, 40, '#1565c0');
    // 하리 폭 (내부) — 팔 안면 사이 = 정확히 1000
    drawDimension(ctx, bx + PT_PX, PADDING + s(V), bx + bw - PT_PX, PADDING + s(V), `${hariBottom.width - PT * 2}`, 40, '#1565c0');
    drawDimension(ctx, bx + bw - PT_PX, PADDING + s(V), PADDING + s(H), PADDING + s(V), `${H - hariBottomPos - hariBottom.width + PT}`, 40, '#1565c0');

    // 하단벽 구간 검산식 캡션
    drawCalcCaption(ctx, `(${cornBL ? cornBL.width : 100}+${bWallCapLeft.join('+4+')}+4 = ${hariBottomPos + PT})`, (PADDING + bx + PT_PX) / 2, PADDING + s(V) + 53);
    drawCalcCaption(ctx, `(100+${bWallCapRight.join('+4+')}+4${cornBR ? '+' + cornBR.width : ''} = ${H - hariBottomPos - hariBottom.width + PT})`, (bx + bw + PADDING + s(H)) / 2, PADDING + s(V) + 53);

    const bVLen = hariBottom.height!;
    const bHLen = hariBottom.width - PT * 2;

    // CW 1: 좌측V (↑, -π/2): yangsu→corner (#31=1번)
    drawHariPanelLine(ctx, bVLen, bx + PT_PX / 2, by + bh - PT_PX, -Math.PI / 2, 'yangsu', 'corner');

    // CW 2: 상단H 수동 분할 — #33=4번(yangsu/corner,0°), #34=8번(eumsu/yangsu,-π)
    const bCW2Sizes = splitPanels(bHLen + PT);
    let bCW2X = bx + bw; // 우팔 바깥면부터: 441(첫)이 우팔 기둥을 덮음 — 실제 크기 렌더
    bCW2Sizes.forEach((size, i) => {
      const pl = s(size);
      const isFirst = i === 0;
      const lt = isFirst ? 'yangsu' : 'eumsu';
      const rt = isFirst ? 'corner' : 'yangsu';
      const angle = isFirst ? 0 : -Math.PI;
      bCW2X -= pl;
      drawPanel(ctx, bCW2X + pl / 2, by - PT_PX / 2, pl, PT_PX, angle, lt, rt, `${size}`);
      bCW2X -= s(CONFIG.PANEL_SPACE);
    });

    // CW 3: 우측V 수동 분할 — 팔이 하단벽 밴드까지 관통, 아래 끝은 corner 소유 (원래 확정 구조)
    const bCW3Sizes = splitPanels(bVLen);
    let bCW3Y = by;
    bCW3Sizes.forEach((size, i) => {
      const pl = s(size);
      const isLast = i === bCW3Sizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const angle = isLast ? -Math.PI / 2 : -Math.PI * 1.5;
      drawPanel(ctx, bx + bw - PT_PX / 2, bCW3Y + pl / 2, pl, PT_PX, angle, lt, rt, `${size}`);
      bCW3Y += pl + s(CONFIG.PANEL_SPACE);
    });

    } else {
      // 하단 하리 없음: 전체 가로 치수 + 검산식 (선은 이동된 우측벽 외면까지)
      drawDimension(ctx, PADDING, PADDING + s(V), PADDING + s(H), PADDING + s(V), `${H}`, 40, '#1565c0');
      if (!hasBL && !hasBR) drawCalcCaption(ctx, `(100+${bWallCapLeft.join('+4+')}+4 = ${H})`, PADDING + s(H) / 2, PADDING + s(V) + 53);
    }

    if (hariRight) {
    // ── 우측 하리 (ㄷ, 오른쪽 벽으로 열림)
    // CCW: 상단H(←) → 좌측V(↑) → 하단H(→)
    // CW:  하단H(←) → 좌측V(↓) → 상단H(→)
    const rx = PADDING + s(H) - s(hariRight.height!);
    const ry = PADDING + s(hariRightPos);
    const rw = s(hariRight.height!);
    const rh = s(hariRight.width);

    ctx.save();
    ctx.strokeStyle = '#2e7d32'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(rx - PT_PX, ry, rw + PT_PX, rh);
    ctx.setLineDash([]); ctx.restore();
    p.bounds.push({ id: hariRight.id, x: rx - PT_PX, y: ry, w: rw + PT_PX, h: rh, label: '우측', wallSide: 'right' });

    ctx.save();
    ctx.fillStyle = '#2e7d32'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(`우측 하리 CW (${hariRight.width - PT * 2}×${hariRight.height!})`, rx + rw / 2, ry - 4);
    ctx.restore();

    drawDimension(ctx, rx, ry + rh, rx + rw, ry + rh, `${hariRight.height!}`, 45, '#2e7d32');
    // 하리 폭 (내부, 좌측V 팔 바깥쪽)
    drawDimension(ctx, rx - PT_PX, ry + PT_PX, rx - PT_PX, ry + rh - PT_PX, `${hariRight.width - PT * 2}`, -45, '#2e7d32');
    // 상단벽~하리내벽, 하리내벽~하단벽 거리 (바깥쪽, 하리 두께 포함)
    drawDimension(ctx, PADDING + s(H), PADDING, PADDING + s(H), ry + PT_PX, `${hariRightPos + PT}`, 80, '#2e7d32');
    drawDimension(ctx, PADDING + s(H), ry + PT_PX, PADDING + s(H), ry + rh - PT_PX, `${hariRight.width - PT * 2}`, 80, '#2e7d32');
    drawDimension(ctx, PADDING + s(H), ry + rh - PT_PX, PADDING + s(H), PADDING + s(V), `${V - hariRightPos - hariRight.width + PT}`, 80, '#2e7d32');

    // 우측벽 구간 검산식 캡션
    drawCalcCaption(ctx, `(${cornTR ? cornTR.height + '+' : ''}${rWallCapAbove.join('+4+')}+4+100 = ${hariRightPos + PT})`, PADDING + s(H) + 93, PADDING + s((hariRightPos + PT) / 2), true);
    drawCalcCaption(ctx, `(${rWallCapBelow.join('+4+')}+4+${cornBR ? cornBR.height : 100} = ${V - hariRightPos - hariRight.width + PT})`, PADDING + s(H) + 93, PADDING + s((hariRightPos + hariRight.width - PT + V) / 2), true);

    const rHLen = hariRight.height!;
    const rVLen = hariRight.width - PT * 2;

    // CW 1: 하단H (←, -π): yangsu→corner (#37=1번)
    drawHariPanelLine(ctx, rHLen, rx + rw - PT_PX, ry + rh - PT_PX / 2, -Math.PI, 'yangsu', 'corner');

    // CW 2: 좌측V 수동 분할 — #39=4번(yangsu/corner,-π/2), #40=E/Y번
    const rCW2Sizes = splitPanels(rVLen + PT);
    let rCW2Y = ry;
    rCW2Sizes.forEach((size, i) => {
      const pl = s(size);
      const isFirst = i === 0;
      const lt = isFirst ? 'yangsu' : 'eumsu';
      const rt = isFirst ? 'corner' : 'yangsu';
      const angle = isFirst ? -Math.PI / 2 : -Math.PI * 1.5;
      drawPanel(ctx, rx - PT_PX / 2, rCW2Y + pl / 2, pl, PT_PX, angle, lt, rt, `${size}`);
      rCW2Y += pl + s(CONFIG.PANEL_SPACE);
    });

    // CW 3: 상단H 수동 분할 — #41=1번(yangsu/eumsu,0°), #42=6번(corner/yangsu,-π)
    const rCW3Sizes = splitPanels(rHLen);
    const rCW3BaseX = rx;
    const rCW3Y = ry + PT_PX / 2;
    let rCW3X = rCW3BaseX;
    rCW3Sizes.forEach((size, i) => {
      const pl = s(size);
      const isLast = i === rCW3Sizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const angle = isLast ? -Math.PI : 0;
      drawPanel(ctx, rCW3X + pl / 2, rCW3Y, pl, PT_PX, angle, lt, rt, `${size}`);
      rCW3X += pl + s(CONFIG.PANEL_SPACE);
    });

    } else {
      // 우측 하리 없음: 전체 세로 치수 + 검산식
      drawDimension(ctx, PADDING + s(H), PADDING, PADDING + s(H), PADDING + s(V), `${V}`, 80, '#2e7d32');
      if (!hasTR && !hasBR) drawCalcCaption(ctx, `(${rWallCapAbove.join('+4+')}+4+100 = ${V})`, PADDING + s(H) + 93, PADDING + s(V) / 2, true);
    }

    if (hariTop) {
    // ── 상단 하리 (ㄷ, 위쪽 벽으로 열림)
    // CCW: 좌측V(↓) → 하단H(←) → 우측V(↑)
    // CW:  우측V(↓) → 하단H(→) → 좌측V(↑)
    const ttx = PADDING + s(hariTopPos); // 명목 위치 [position, position+width]
    const tty = PADDING;
    const ttw = s(hariTop.width);
    const tth = s(hariTop.height!);

    ctx.save();
    ctx.strokeStyle = '#ff6f00'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(ttx, tty, ttw, tth + PT_PX);
    ctx.setLineDash([]); ctx.restore();
    p.bounds.push({ id: hariTop.id, x: ttx, y: tty, w: ttw, h: tth + PT_PX, label: '상단', wallSide: 'top' });

    ctx.save();
    ctx.fillStyle = '#ff6f00'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText(`상단 하리 CW (${hariTop.width - PT * 2}×${hariTop.height!})`, ttx + ttw / 2, tty + tth + 4);
    ctx.restore();

    drawDimension(ctx, ttx + ttw, tty, ttx + ttw, tty + tth, `${hariTop.height!}`, 45, '#ff6f00');
    // 하리 폭 (내부, 하단H 팔 바깥쪽)
    drawDimension(ctx, ttx + PT_PX, tty + tth + PT_PX, ttx + ttw - PT_PX, tty + tth + PT_PX, `${hariTop.width - PT * 2}`, 45, '#ff6f00');
    // 좌측벽~하리내벽, 하리내벽~우측벽 거리 (바깥쪽, 하리 두께 포함)
    // 전체 가로 치수
    const topLeftDist = hariTopPos + PT;
    const topRightDist = H - hariTopPos - hariTop.width + PT;
    const topHariW = hariTop.width - PT * 2;

    drawDimension(ctx, PADDING, PADDING, ttx + PT_PX, PADDING, `${topLeftDist}`, -40, '#ff6f00');
    const topLeftSum = topPanelSizesLeft.join('+4+');
    ctx.save();
    ctx.fillStyle = '#888'; ctx.font = '9px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(`(${cornTL ? cornTL.width + '+' : ''}${topLeftSum}+4+100 = ${topLeftDist})`, (PADDING + ttx + PT_PX) / 2, PADDING - 53);
    ctx.restore();
    drawDimension(ctx, ttx + ttw - PT_PX, PADDING, PADDING + s(H), PADDING, `${topRightDist}`, -40, '#ff6f00');
    const topRightSum = topPanelSizesRight.join('+4+');
    ctx.save();
    ctx.fillStyle = '#888'; ctx.font = '9px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(`(${topRightSum}+4+${cornTR ? cornTR.width : 100} = ${topRightDist})`, (ttx + ttw + PADDING + s(H)) / 2, PADDING - 53);
    ctx.restore();
    // 하리 폭 치수 (2100과 1800 사이)
    drawDimension(ctx, ttx + PT_PX, PADDING, ttx + ttw - PT_PX, PADDING, `${hariTop.width - PT * 2}`, -40, '#ff6f00');

    const tVLen = hariTop.height!;
    const tHLen = hariTop.width - PT * 2;

    // CW 1: 우측V (↓, -3π/2): none→corner
    drawHariPanelLine(ctx, tVLen, ttx + ttw - PT_PX / 2, tty + PT_PX, -Math.PI * 1.5, 'yangsu', 'corner');

    // CW 2: 하단H 수동 분할 — 첫:4번(yangsu/corner,-π), 나머지:8번(eumsu/yangsu,0°)
    const tBottomLen = tHLen + PT;
    const tCW2Sizes = splitPanels(tBottomLen);
    let tCW2X = ttx; // 왼팔 바깥면부터: 441(첫)이 왼팔 기둥을 덮음 — 실제 크기 렌더
    tCW2Sizes.forEach((size, i) => {
      const pl = s(size);
      const isFirst = i === 0;
      const lt = isFirst ? 'yangsu' : 'eumsu';
      const rt = isFirst ? 'corner' : 'yangsu';
      const a = isFirst ? -Math.PI : 0;
      drawPanel(ctx, tCW2X + pl / 2, tty + tth + PT_PX / 2, pl, PT_PX, a, lt, rt, `${size}`);
      tCW2X += pl + s(CONFIG.PANEL_SPACE);
    });

    // CW 3: 좌측V (↑, -π/2): 수동 분할 — #47=8번(eumsu/yangsu), #48=6번(corner/yangsu)
    const tCW3Sizes = splitPanels(tVLen);
    let tCW3Y = tty + tth;
    tCW3Sizes.forEach((size, i) => {
      const pl = s(size);
      tCW3Y -= pl;
      const isLast = i === tCW3Sizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const angle = isLast ? -Math.PI * 1.5 : -Math.PI / 2;
      drawPanel(ctx, ttx + PT_PX / 2, tCW3Y + pl / 2, pl, PT_PX, angle, lt, rt, `${size}`);
      tCW3Y -= s(CONFIG.PANEL_SPACE);
    });
    } else {
      // 상단 하리 없음: 전체 가로 치수 + 검산식 (선은 이동된 우측벽 외면까지)
      drawDimension(ctx, PADDING, PADDING, PADDING + s(H), PADDING, `${H}`, -40, '#ff6f00');
      if (!hasTL && !hasTR) drawCalcCaption(ctx, `(${topPanelSizesLeft.join('+4+')}+4+100 = ${H})`, PADDING + s(H) / 2, PADDING - 53);
    }

    // 코너형 하리 (모서리 기둥)
    haris.filter(hh => hh.hariType === 'corner').forEach(hh => drawCornerHari(ctx, hh, p.bounds, true));
    markInvalidJoints(ctx, drawnPanels);

    return drawnPanels;
}

export function drawSceneCCW(ctx: CanvasRenderingContext2D, p: SceneParams): DrawnPanel[] {
  const haris = p.haris;
    panelCounter = 0;
    hariLineRec = null; // CCW는 오버라이드 미적용 — 기록 안 함
    drawnPanels = [];
    // CCW에서는 오버라이드 적용 안함
    currentOverrides = {};
    currentRules = {};

    const W = PADDING * 2 + s(H) + 120;
    const HH = PADDING * 2 + s(V) + 180;

    ctx.fillStyle = '#f5f5f5'; ctx.fillRect(0, 0, W, HH);
    ctx.fillStyle = '#fff'; ctx.fillRect(PADDING, PADDING, s(H), s(V));

    const hariMiddle = haris.find(h => h.wallSide === 'left');
    const hariBottom = haris.find(h => h.wallSide === 'bottom');
    const hariRight = haris.find(h => h.wallSide === 'right');
    const hariTop = haris.find(h => h.wallSide === 'top');
    const allHaris = haris;
    const hariMiddlePos = hariMiddle ? hariMiddle.position! : Number.POSITIVE_INFINITY;
    const hariBottomPos = hariBottom ? hariBottom.position! : Number.POSITIVE_INFINITY;
    const hariRightPos = hariRight ? hariRight.position! : Number.POSITIVE_INFINITY;
    const hariTopPos = hariTop ? hariTop.position! : Number.POSITIVE_INFINITY;
    const cornTL = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'topLeft');
    const cornTR = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'topRight');
    const cornBL = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'bottomLeft');
    const cornBR = haris.find(hh => hh.hariType === 'corner' && hh.corner === 'bottomRight');
    const hasTL = !!cornTL, hasTR = !!cornTR, hasBL = !!cornBL, hasBR = !!cornBR;
    const leftOwnsBL = !!hariMiddle && !hasBL; // CW와 동일 — 분할 길이에 좌하단 관통분 포함
    // 코너 하리 제외 폭 보정 (CCW 꺾임 규칙): 상단 우상 칸 소유(4100까지), 좌측 좌상 칸 소유(900부터),
    // 하단 우하는 수직 바깥면(4000)까지, 우측 우상은 수평 아랫면(1000)부터
    const walls = calculateAllWalls(H, V, CONFIG, [], PT, allHaris, leftOwnsBL ? PT : 0,
      { 'top:topRight': -PT, 'left:topLeft': -PT, 'bottom:bottomRight': -PT, 'right:topRight': -PT,
        'left:bottomLeft': hariMiddle ? -PT : 0 }); // 좌측 하리 아래 구간은 −PT 이동하므로 분할에 PT 포함

    // ── 상단벽 (→, 좌→우) CCW
    const lastTopIdx = walls.top.length - 1;
    const hariTopRightX = hariTopPos + (hariTop?.width ?? 0);
    const tCapL: string[] = [];
    const tCapR: string[] = [];
    walls.top.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === lastTopIdx;
      let pl = s(p.size);
      let cx = PADDING + s(PT + p.x) + s(p.size) / 2;
      let lt = isFirst ? 'yangsu' : (p.leftType || 'yangsu');
      let rt = isLast ? 'corner' : (p.rightType || 'eumsu'); // 우상 칸(외곽 코너 또는 코너 하리 칸) 소유

      const isBeforeHari = Math.abs((p.x + p.size) - hariTopPos) < 50;
      if (isBeforeHari) {
        rt = 'corner'; // 실제 크기로 왼팔 칸을 덮음 (…+647+4 = 개구부 2100)
      }
      const isAfterHari = Math.abs((PT + p.x) - hariTopRightX) < 50;
      if (isAfterHari) {
        lt = 'yangsu'; // 팔(441이 덮는 기둥) 몸통에 양수로 결합
      }
      // isLast(우측 끝): 분할 구간이 외곽 5000까지라 우상단 코너를 실제 크기로 소유
      // 라벨 = 주문 크기: 좌상단 코너는 좌측벽 소유라 100+900+441+647+12 = 2100 (747이면 2200 초과)
      const topLabel = p.size;
      (p.x < hariTopPos ? tCapL : tCapR).push(`${topLabel}`);
      drawPanel(ctx, cx, PADDING + PT_PX / 2, pl, PT_PX, 0, lt, rt, `${topLabel}`);
    });

    // ── 우측벽 (↓, 위→아래) CCW
    const lastRightIdx = walls.right.length - 1;
    const hariRightTopY = hariRightPos;
    const hariRightBottomY = hariRightPos + (hariRight?.width ?? 0);
    const rWallCapAbove: string[] = [];
    const rWallCapBelow: string[] = [];
    walls.right.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === lastRightIdx;
      let pl = s(p.size);
      let eSize = p.size;
      let cy = PADDING + s(p.y) + s(p.size) / 2 + PT_PX; // PT만큼 아래로
      let lt = p.leftType || 'yangsu';
      let rt = isLast ? 'corner' : (p.rightType || 'eumsu'); // 우하 칸(외곽 코너 또는 코너 하리 칸) 소유
      // 우상 모서리 하리: 제외 폭 보정으로 첫 패널이 코너 수평 아랫면(1000)에서 시작 — 확장 없음
      const isBeforeHariRight = Math.abs((p.y + p.size) - hariRightTopY) < 200;
      // 전역 +PT 시프트만으로 팔(100)을 덮음 — 위로 확장하면 앞 패널(441)과 100 겹침
      if (isBeforeHariRight) {
        rt = 'corner';
      }
      // 하리 아래 패널: 팔이 관통(코너 소유) → 벽은 팔 안쪽면부터, 양수가 팔 몸통에 결합
      const isAfterHariRight = Math.abs(p.y - hariRightBottomY) < 200;
      if (p.y >= hariRightBottomY - 200) {
        if (isAfterHariRight) { lt = 'yangsu'; }
      }
      // isLast(하단): +PT 시프트로 외곽 5000에서 끝나며 우하단 코너 소유 — 확장 불필요
      const ccwRLabel = p.size; // 분할이 이미 팔 포함(892)이라 +PT 보정 제거
      (p.y < hariRightTopY ? rWallCapAbove : rWallCapBelow).push(`${ccwRLabel}`);
      drawPanel(ctx, PADDING + s(H - PT) + PT_PX / 2, cy,
        pl, PT_PX, -Math.PI * 1.5, lt, rt, `${ccwRLabel}`);
    });

    // ── 하단벽 (←, 우→좌) CCW
    const hariBottomRightX = hariBottomPos + (hariBottom?.width ?? 0);
    const lastBottomIdxCCW = walls.bottom.length - 1;
    const bWallCapLeft: string[] = [];
    const bWallCapRight: string[] = [];
    walls.bottom.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === lastBottomIdxCCW;
      let pl = s(p.size);
      let cx = PADDING + s(p.x) + s(p.size) / 2;
      const cy = PADDING + s(V - PT) + PT_PX / 2; // 명목 위치 [4900~5000]
      let lt = isFirst ? 'yangsu' : (p.leftType || 'yangsu');
      let rt = isFirst ? 'corner' : (p.rightType || 'eumsu'); // 좌하 칸(외곽 코너 또는 코너 하리 칸) 소유

      // 하리 오른쪽 첫 패널: 실제 크기로 오른팔 칸을 덮음 (구간이 팔 바깥면 3100부터 시작)
      const isHariRightAdj = Math.abs(p.x - (hariBottomRightX - PT)) < 10;
      if (isHariRightAdj) {
        rt = 'corner';
      }
      // isLast(우측 끝): 우측벽 내면 4900에서 끝 (우하단 코너는 우측벽 소유)
      const ccwBLabel = p.size; // 분할이 이미 팔 포함이라 +PT 보정 제거
      (p.x < hariBottomPos ? bWallCapLeft : bWallCapRight).push(`${ccwBLabel}`);
      drawPanel(ctx, cx, cy, pl, PT_PX, -Math.PI, lt, rt, `${ccwBLabel}`);
    });

    // ── 좌측벽 (↑, 아래→위) CCW
    const hariEndYCCW = hariMiddlePos + (hariMiddle?.width ?? 0);
    const hariBottomIdx = walls.left.findIndex(p => p.y >= hariEndYCCW - PT - 1);
    const lastLeftIdxCCW = walls.left.length - 1;
    const lWallCapAbove: string[] = [];
    const lWallCapBelow: string[] = [];
    walls.left.forEach((p, i) => {
      const isLast = i === lastLeftIdxCCW; // 맨 아래
      let pl = s(p.size);
      let cy = PADDING + s(p.y) + s(p.size) / 2;
      let lt = p.leftType || 'yangsu';
      let rt = i === 0 ? 'corner' : (p.rightType || 'eumsu'); // 좌상 칸(외곽 코너 또는 코너 하리 칸) 소유

      const isHariAdj = (i === hariBottomIdx);
      if (isHariAdj) {
        rt = 'corner';
      }
      // 하리 아래 구간: 아래팔 칸(벽이 덮음)부터 — 실제 크기로 −PT 이동
      // (끝은 하단벽 윗면 4900, 좌하 코너 하리면 수평 바깥면 4000 — 분할 길이에 PT 포함됨)
      if (hariMiddle && i >= hariBottomIdx) {
        cy -= PT_PX;
      }
      // isLast(맨 아래): 하단벽 윗면(4900)에서 끝 — 좌하단 코너는 하단벽 소유 (확장 없음)
      const ccwLLabel = p.size;
      ((!hariMiddle || i < hariBottomIdx) ? lWallCapAbove : lWallCapBelow).push(`${ccwLLabel}`);
      drawPanel(ctx, PADDING + PT_PX / 2, cy,
        pl, PT_PX, -Math.PI / 2, lt, rt, `${ccwLLabel}`);
    });

    if (hariMiddle) {
    // ── 좌측 하리 CCW
    const hx = PADDING;
    const hy = PADDING + s(hariMiddlePos);
    const hw = s(hariMiddle.height);
    const hh = s(hariMiddle.width);

    ctx.save();
    ctx.strokeStyle = '#e53935'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(hx, hy, hw, hh);
    ctx.setLineDash([]); ctx.restore();

    ctx.save();
    ctx.fillStyle = '#e53935'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(`좌측 하리 CCW (${hariMiddle.width - PT * 2}×${hariMiddle.height})`, hx + hw / 2, hy - 4);
    ctx.restore();
    p.bounds.push({ id: hariMiddle.id, x: hx, y: hy, w: hw + PT_PX, h: hh, label: '좌측', wallSide: 'left' });

    drawDimension(ctx, hx, hy + hh, hx + hw, hy + hh, `${hariMiddle.height}`, 45, '#e53935');
    drawDimension(ctx, hx + hw + PT_PX, hy + PT_PX, hx + hw + PT_PX, hy + hh - PT_PX, `${hariMiddle.width - PT * 2}`, 45, '#e53935');
    // 벽~하리 거리 치수선 (하단 구간은 내려간 하단벽 두께까지 +PT 연장)
    drawDimension(ctx, PADDING, PADDING, PADDING, hy + PT_PX, `${hariMiddlePos + PT}`, -40, '#e53935');
    drawDimension(ctx, PADDING, hy + PT_PX, PADDING, hy + hh - PT_PX, `${hariMiddle.width - PT * 2}`, -40, '#e53935');
    drawDimension(ctx, PADDING, hy + hh - PT_PX, PADDING, PADDING + s(V), `${V - hariMiddlePos - hariMiddle.width + PT}`, -40, '#e53935');

    // 좌측벽 구간 검산식 캡션
    drawCalcCaption(ctx, `(${cornTL ? cornTL.height + '+' : ''}${lWallCapAbove.join('+4+')}+4+100 = ${hariMiddlePos + PT})`, 8, PADDING + s((hariMiddlePos + PT) / 2), true);
    drawCalcCaption(ctx, `(100+${lWallCapBelow.join('+4+')}+4${cornBL ? '+' + cornBL.height : ''} = ${V - hariMiddlePos - hariMiddle.width + PT})`, 8, PADDING + s((hariMiddlePos + hariMiddle.width - PT + V) / 2), true);

    const hLen = hariMiddle.height;
    const vLen = hariMiddle.width - PT * 2;

    // CCW: 하단H(→) → 우측V(↓→↑) → 상단H(←)
    // 하단H: 수동 분할 - 마지막 패널 6번(corner/yangsu) 180도
    const hBottomSizes = splitPanels(hLen);
    let hBottomCurX = hx + PT_PX;
    hBottomSizes.forEach((size, i) => {
      const pl = s(size);
      const isLast = i === hBottomSizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const a = isLast ? -Math.PI : 0;
      drawPanel(ctx, hBottomCurX + pl / 2, hy + hh - PT_PX / 2, pl, PT_PX, a, lt, rt, `${size}`);
      hBottomCurX += pl + s(CONFIG.PANEL_SPACE);
    });
    drawHariPanelLine(ctx, vLen + PT, hx + hw + PT_PX / 2, hy, -Math.PI * 1.5, 'corner', 'yangsu', 'yangsu');
    // 상단H: 수동 분할
    const hTopSizes = splitPanels(hLen);
    drawPanel(ctx, hx + s(hTopSizes[0]) / 2, hy + PT_PX / 2,
      s(hTopSizes[0]), PT_PX, -Math.PI, 'yangsu', 'corner', `${hTopSizes[0]}`);
    if (hTopSizes.length > 1) {
      const nextX = hx + s(hTopSizes[0]) + s(CONFIG.PANEL_SPACE);
      drawPanel(ctx, nextX + s(hTopSizes[1]) / 2, hy + PT_PX / 2,
        s(hTopSizes[1]), PT_PX, 0, 'eumsu', 'yangsu', `${hTopSizes[1]}`);
    }

    } else {
      drawDimension(ctx, PADDING, PADDING, PADDING, PADDING + s(V), `${V}`, -40, '#e53935');
      if (!hasTL && !hasBL) drawCalcCaption(ctx, `(${lWallCapAbove.join('+4+')}+4+100 = ${V})`, 8, PADDING + s(V) / 2, true);
    }

    if (hariBottom) {
    // ── 하단 하리 CCW
    const bx = PADDING + s(hariBottomPos);
    const by = PADDING + s(V) - s(hariBottom.height!); // 명목 위치
    const bw = s(hariBottom.width);
    const bh = s(hariBottom.height!);

    ctx.save();
    ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(bx, by - PT_PX, bw, bh + PT_PX);
    ctx.setLineDash([]); ctx.restore();

    ctx.save();
    ctx.fillStyle = '#1565c0'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(`하단 하리 CCW (${hariBottom.width - PT * 2}×${hariBottom.height!})`, bx + bw / 2, by - PT_PX - 4);
    ctx.restore();
    p.bounds.push({ id: hariBottom.id, x: bx, y: by - PT_PX, w: bw, h: bh + PT_PX, label: '하단', wallSide: 'bottom' });

    drawDimension(ctx, bx + PT_PX, by - PT_PX, bx + bw - PT_PX, by - PT_PX, `${hariBottom.width - PT * 2}`, -45, '#1565c0');
    drawDimension(ctx, bx, by, bx, by + bh, `${hariBottom.height!}`, -45, '#1565c0');
    // 벽~하리 거리 치수선 (CCW 하단 하리는 PT 이동 없음 → position+PT, 내려간 밴드/뱃지와 겹침 방지 오프셋 80)
    drawDimension(ctx, PADDING, PADDING + s(V), bx + PT_PX, PADDING + s(V), `${hariBottomPos + PT}`, 80, '#1565c0');
    drawDimension(ctx, bx + PT_PX, PADDING + s(V), bx + bw - PT_PX, PADDING + s(V), `${hariBottom.width - PT * 2}`, 80, '#1565c0');
    drawDimension(ctx, bx + bw - PT_PX, PADDING + s(V), PADDING + s(H), PADDING + s(V), `${H - hariBottomPos - hariBottom.width + PT}`, 80, '#1565c0');

    // 하단벽 구간 검산식 캡션
    drawCalcCaption(ctx, `(${cornBL ? cornBL.width : 100}+${bWallCapLeft.join('+4+')}+4 = ${hariBottomPos + PT})`, (PADDING + bx + PT_PX) / 2, PADDING + s(V) + 93);
    drawCalcCaption(ctx, `(${bWallCapRight.join('+4+')}+4+${cornBR ? cornBR.width : 100} = ${H - hariBottomPos - hariBottom.width + PT})`, (bx + bw - PT_PX + PADDING + s(H)) / 2, PADDING + s(V) + 93);

    const bVLen = hariBottom.height!;
    const bHLen = hariBottom.width - PT * 2;

    // CCW: 우측V(↑) → 상단H(→) → 좌측V(↓)
    // 우측V: 수동 분할 - 마지막 패널 6번(corner/yangsu) 90도
    const bRightVSizes = splitPanels(bVLen);
    let bRightVCurY = by + bh - PT_PX;
    bRightVSizes.forEach((size, i) => {
      const pl = s(size);
      const isLast = i === bRightVSizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const a = isLast ? -Math.PI * 1.5 : -Math.PI / 2;
      drawPanel(ctx, bx + bw - PT_PX / 2, bRightVCurY - pl / 2, pl, PT_PX, a, lt, rt, `${size}`);
      bRightVCurY -= pl + s(CONFIG.PANEL_SPACE);
    });
    drawHariPanelLine(ctx, bHLen + PT, bx, by - PT_PX / 2, 0, 'corner', 'yangsu', 'yangsu');
    drawHariPanelLine(ctx, bVLen, bx + PT_PX / 2, by, -Math.PI * 1.5, 'yangsu', 'corner');

    } else {
      drawDimension(ctx, PADDING, PADDING + s(V), PADDING + s(H), PADDING + s(V), `${H}`, 80, '#1565c0');
      if (!hasBL && !hasBR) drawCalcCaption(ctx, `(100+${bWallCapLeft.join('+4+')}+4 = ${H})`, PADDING + s(H) / 2, PADDING + s(V) + 93);
    }

    if (hariRight) {
    // ── 우측 하리 CCW
    const rx = PADDING + s(H) - s(hariRight.height!); // 명목 위치
    const ry = PADDING + s(hariRightPos);
    const rw = s(hariRight.height!);
    const rh = s(hariRight.width);

    ctx.save();
    ctx.strokeStyle = '#2e7d32'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(rx - PT_PX, ry, rw + PT_PX, rh);
    ctx.setLineDash([]); ctx.restore();

    ctx.save();
    ctx.fillStyle = '#2e7d32'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(`우측 하리 CCW (${hariRight.width - PT * 2}×${hariRight.height!})`, rx + rw / 2, ry - 4);
    ctx.restore();
    p.bounds.push({ id: hariRight.id, x: rx - PT_PX, y: ry, w: rw + PT_PX, h: rh, label: '우측', wallSide: 'right' });

    drawDimension(ctx, rx, ry + rh, rx + rw, ry + rh, `${hariRight.height!}`, 45, '#2e7d32');
    // 하리 폭 (내부, 좌측V 팔 바깥쪽)
    drawDimension(ctx, rx - PT_PX, ry + PT_PX, rx - PT_PX, ry + rh - PT_PX, `${hariRight.width - PT * 2}`, -45, '#2e7d32');
    // 벽~하리 거리 치수선 (하단 구간은 내려간 하단벽까지 +PT 연장)
    drawDimension(ctx, PADDING + s(H), PADDING, PADDING + s(H), ry + PT_PX, `${hariRightPos + PT}`, 80, '#2e7d32');
    drawDimension(ctx, PADDING + s(H), ry + PT_PX, PADDING + s(H), ry + rh - PT_PX, `${hariRight.width - PT * 2}`, 80, '#2e7d32');
    drawDimension(ctx, PADDING + s(H), ry + rh - PT_PX, PADDING + s(H), PADDING + s(V), `${V - hariRightPos - hariRight.width + PT}`, 80, '#2e7d32');

    // 우측벽 구간 검산식 캡션
    drawCalcCaption(ctx, `(${cornTR ? cornTR.height + '+' : ''}${rWallCapAbove.join('+4+')}+4+100 = ${hariRightPos + PT})`, PADDING + s(H) + 93, PADDING + s((hariRightPos + PT) / 2), true);
    drawCalcCaption(ctx, `(100+${rWallCapBelow.join('+4+')}+4${cornBR ? '+' + cornBR.height : ''} = ${V - hariRightPos - hariRight.width + PT})`, PADDING + s(H) + 93, PADDING + s((hariRightPos + hariRight.width - PT + V) / 2), true);

    const rHLen = hariRight.height!;
    const rVLen = hariRight.width - PT * 2;

    // CCW: 상단H(←) → 좌측V(↑→↓) → 하단H(→)
    // 상단H 수동 분할
    const rTopSizes = splitPanels(rHLen);
    const rTopBaseX = rx + rw - PT_PX;
    const rTopBaseY = ry + PT_PX / 2;
    drawPanel(ctx, rTopBaseX + Math.cos(-Math.PI) * s(rTopSizes[0]) / 2, rTopBaseY,
      s(rTopSizes[0]), PT_PX, -Math.PI, 'yangsu', 'eumsu', `${rTopSizes[0]}`);
    if (rTopSizes.length > 1) {
      const r2StartX = rTopBaseX + Math.cos(-Math.PI) * (s(rTopSizes[0]) + s(CONFIG.PANEL_SPACE));
      drawPanel(ctx, r2StartX + Math.cos(-Math.PI) * s(rTopSizes[1]) / 2, rTopBaseY,
        s(rTopSizes[1]), PT_PX, 0, 'corner', 'yangsu', `${rTopSizes[1]}`);
    }

    // 좌측V 수동 분할
    const rVSizes = splitPanels(rVLen + PT);
    let rVCurY = ry + PT_PX + s(rVLen + PT);
    rVSizes.forEach((size, i) => {
      const pl = s(size);
      rVCurY -= pl;
      const lt = i === 0 ? 'corner' : 'eumsu';
      const rt = 'yangsu';
      drawPanel(ctx, rx - PT_PX / 2, rVCurY + pl / 2, pl, PT_PX, -Math.PI / 2, lt, rt, `${size}`);
      rVCurY -= s(CONFIG.PANEL_SPACE);
    });

    // 하단H(→)
    drawHariPanelLine(ctx, rHLen, rx, ry + rh - PT_PX / 2, 0, 'yangsu', 'corner');

    } else {
      drawDimension(ctx, PADDING + s(H), PADDING, PADDING + s(H), PADDING + s(V), `${V}`, 80, '#2e7d32');
      if (!hasTR && !hasBR) drawCalcCaption(ctx, `(100+${rWallCapAbove.join('+4+')}+4 = ${V})`, PADDING + s(H) + 93, PADDING + s(V) / 2, true);
    }

    if (hariTop) {
    // ── 상단 하리 CCW (개구부 1000 정렬 — 박스 폭 = width)
    const ttx = PADDING + s(hariTopPos); // 명목 위치 [position, position+width]
    const tty = PADDING;
    const ttw = s(hariTop.width);
    const tth = s(hariTop.height!);

    ctx.save();
    ctx.strokeStyle = '#ff6f00'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
    ctx.strokeRect(ttx, tty, ttw, tth + PT_PX);
    ctx.setLineDash([]); ctx.restore();

    ctx.save();
    ctx.fillStyle = '#ff6f00'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText(`상단 하리 CCW (${hariTop.width - PT * 2}×${hariTop.height!})`, ttx + ttw / 2, tty + tth + PT_PX + 4);
    ctx.restore();
    p.bounds.push({ id: hariTop.id, x: ttx, y: tty, w: ttw, h: tth + PT_PX, label: '상단', wallSide: 'top' });

    drawDimension(ctx, ttx + ttw, tty, ttx + ttw, tty + tth, `${hariTop.height!}`, 45, '#ff6f00');
    // 하리 폭 (내부, 하단H 팔 바깥쪽) — 팔 안면 사이 = 정확히 1000
    drawDimension(ctx, ttx + PT_PX, tty + tth + PT_PX, ttx + ttw - PT_PX, tty + tth + PT_PX, `${hariTop.width - PT * 2}`, 45, '#ff6f00');
    // 벽~하리 거리 치수선
    const ccwTopLeftDist = hariTopPos + PT;
    const ccwTopRightDist = H - hariTopPos - hariTop.width;
    const ccwTopHariW = hariTop.width - PT * 2;
    drawDimension(ctx, PADDING, PADDING, ttx + PT_PX, PADDING, `${ccwTopLeftDist}`, -40, '#ff6f00');
    drawDimension(ctx, ttx + PT_PX, PADDING, ttx + ttw - PT_PX, PADDING, `${hariTop.width - PT * 2}`, -40, '#ff6f00');
    drawDimension(ctx, ttx + ttw, PADDING, PADDING + s(H), PADDING, `${ccwTopRightDist + PT}`, -40, '#ff6f00');

    // 상단벽 구간 검산식 캡션 (앞 100 = 좌상단 코너를 소유한 좌측벽 두께)
    drawCalcCaption(ctx, `(${cornTL ? cornTL.width + '+' : '100+'}${tCapL.join('+4+')}+4 = ${ccwTopLeftDist})`, (PADDING + ttx + PT_PX) / 2, PADDING - 53);
    drawCalcCaption(ctx, `(${tCapR.join('+4+')}+4+${cornTR ? cornTR.width : 100} = ${ccwTopRightDist + PT})`, (ttx + ttw + PADDING + s(H)) / 2, PADDING - 53);

    const tVLen = hariTop.height!;
    const tHLen = hariTop.width - PT * 2;

    // CCW: 좌측V(↓) → 하단H(←→) → 우측V(↑)
    // 좌측V: 수동 분할 - 마지막 패널 6번(corner/yangsu)
    const tLeftVSizes = splitPanels(tVLen);
    let tLeftVCurY = tty + PT_PX;
    tLeftVSizes.forEach((size, i) => {
      const pl = s(size);
      const isLast = i === tLeftVSizes.length - 1;
      const lt = isLast ? 'corner' : 'yangsu';
      const rt = isLast ? 'yangsu' : 'eumsu';
      const a = isLast ? -Math.PI / 2 : -Math.PI * 1.5;
      drawPanel(ctx, ttx + PT_PX / 2, tLeftVCurY + pl / 2, pl, PT_PX, a, lt, rt, `${size}`);
      tLeftVCurY += pl + s(CONFIG.PANEL_SPACE);
    });
    // 하단H 수동 분할 — 441 오른쪽 이동 (CW1 겹침)
    const tBottomLen = tHLen + PT;
    const tCCWBSizes = splitPanels(tBottomLen);
    let tCCWBX = ttx + ttw;
    let tCCWPrevRt = '';
    tCCWBSizes.forEach((size, i) => {
      const pl = s(size);
      const isFirst = i === 0;
      const lt = isFirst ? 'corner' : (tCCWPrevRt === 'yangsu' ? 'eumsu' : 'yangsu');
      const rt = 'yangsu';
      tCCWPrevRt = rt;
      tCCWBX -= pl;
      // 실제 크기 렌더: 441(첫)이 우팔 기둥을 덮고, 651은 왼팔 안면까지 — 이동/확장 없음
      drawPanel(ctx, tCCWBX + pl / 2, tty + tth + PT_PX / 2, pl, PT_PX, -Math.PI, lt, rt, `${size}`);
      tCCWBX -= s(CONFIG.PANEL_SPACE);
    });
    // 우측V(↑) — 개구부 1000 정렬: 팔을 안쪽으로 (441이 팔 기둥을 덮음)
    drawHariPanelLine(ctx, tVLen, ttx + ttw - PT_PX / 2, tty + tth, -Math.PI / 2, 'yangsu', 'corner');

    } else {
      drawDimension(ctx, PADDING, PADDING, PADDING + s(H), PADDING, `${H}`, -40, '#ff6f00');
      if (!hasTL && !hasTR) drawCalcCaption(ctx, `(${tCapL.join('+4+')}+4+100 = ${H})`, PADDING + s(H) / 2, PADDING - 53);
    }

    // 코너형 하리 (모서리 기둥)
    haris.filter(hh => hh.hariType === 'corner').forEach(hh => drawCornerHari(ctx, hh, p.bounds));
    markInvalidJoints(ctx, drawnPanels);


    return drawnPanels;
}
